// src/pages/trading-portal/TradeExecution/TradingBotPage.js
// Trading Bot Management - 4-tab interface: Bot Management, Active Positions, Activity Log, Performance

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    BotStatus, BotStatusLabels, BotStatusColors,
    BotStrategyType, BotStrategyTypeLabels, BotStrategyTypeColors,
    PositionSizingMethod, PositionSizingMethodLabels,
    BotLogLevel, BotLogLevelLabels, BotLogLevelColors,
    BotLogEventType, BotLogEventTypeLabels, BotLogEventTypeColors,
    BotPositionStatus, BotPositionStatusLabels, BotPositionStatusColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const INITIAL_BOT_FORM = {
    name: '',
    description: '',
    strategy_type: '',
    broker_connection_id: '',
    min_confidence_score: '',
    order_type: 'MARKET',
    time_in_force: 'DAY',
    position_sizing_method: '',
    fixed_quantity: '',
    portfolio_percent: '',
    risk_per_trade_pct: '',
    max_position_value: '',
    max_open_positions: '',
    max_daily_trades: '',
    max_daily_loss: '',
    stop_loss_pct: '',
    take_profit_pct: '',
    trailing_stop_pct: '',
    max_holding_hours: '',
    exit_on_signal_reversal: false,
    trading_start_time: '',
    trading_end_time: '',
    // Indicator config (MOMENTUM / MEAN_REVERSION)
    ema_fast: '12',
    ema_slow: '26',
    rsi_period: '14',
    volume_multiplier: '1.5',
    rsi_oversold: '30',
    rsi_overbought: '70',
    bb_period: '20',
    bb_std: '2',
};

const TradingBotPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState(null);

    // ==================== TAB 0: BOT MANAGEMENT ====================
    const [bots, setBots] = useState([]);
    const [botsLoading, setBotsLoading] = useState(true);
    const [botsPage, setBotsPage] = useState(0);
    const [botsPerPage, setBotsPerPage] = useState(10);
    const [botsTotal, setBotsTotal] = useState(0);
    const [botDialog, setBotDialog] = useState(false);
    const [botEditing, setBotEditing] = useState(null);
    const [botForm, setBotForm] = useState({ ...INITIAL_BOT_FORM });
    const [brokerConnections, setBrokerConnections] = useState([]);

    // Confirmation dialog for start/pause/stop/delete
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // { action: 'start'|'pause'|'stop'|'delete', bot: {...} }

    // ==================== TAB 1: ACTIVE POSITIONS ====================
    const [positions, setPositions] = useState([]);
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [positionsPage, setPositionsPage] = useState(0);
    const [positionsPerPage, setPositionsPerPage] = useState(10);
    const [positionsTotal, setPositionsTotal] = useState(0);
    const [positionsBotFilter, setPositionsBotFilter] = useState('');
    const [positionsStatusFilter, setPositionsStatusFilter] = useState('');

    // ==================== TAB 2: ACTIVITY LOG ====================
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPage, setLogsPage] = useState(0);
    const [logsPerPage, setLogsPerPage] = useState(10);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsBotFilter, setLogsBotFilter] = useState('');
    const [logsEventTypeFilter, setLogsEventTypeFilter] = useState('');
    const [logsLevelFilter, setLogsLevelFilter] = useState('');
    const logsIntervalRef = useRef(null);

    // ==================== TAB 3: PERFORMANCE ====================
    const [performance, setPerformance] = useState([]);
    const [performanceLoading, setPerformanceLoading] = useState(false);

    // ==================== FORMAT HELPERS ====================
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';
    const formatPercent = (val) => val != null ? `${parseFloat(val).toFixed(2)}%` : '-';

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, add, edit, del] = await Promise.all([
                hasPermission('tr-tee-trading-bots', 'view'),
                hasPermission('tr-tee-trading-bots', 'add'),
                hasPermission('tr-tee-trading-bots', 'edit'),
                hasPermission('tr-tee-trading-bots', 'delete'),
            ]);
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPerms();
    }, []);

    // ==================== TAB 0: BOT MANAGEMENT ====================
    const fetchBots = useCallback(async () => {
        if (!canView) return;
        setBotsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: botsPage + 1, per_page: botsPerPage });
            const res = await axiosInstance.get(`/api/trade-execution/trading-bots?${params.toString()}`);
            if (res.data.success) {
                setBots(res.data.data || []);
                setBotsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch trading bots');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch trading bots');
        } finally {
            setBotsLoading(false);
        }
    }, [canView, botsPage, botsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchBots();
    }, [fetchBots, canView, activeTab]);

    // Fetch broker connections for dropdown
    useEffect(() => {
        const fetchBrokers = async () => {
            try {
                const res = await axiosInstance.get('/api/trade-execution/broker-connections');
                if (res.data.success) setBrokerConnections(res.data.data || []);
            } catch (err) { /* ignore */ }
        };
        if (canView) fetchBrokers();
    }, [canView]);

    const handleBotDialogOpen = (bot = null) => {
        if (bot) {
            setBotEditing(bot);
            setBotForm({
                name: bot.name || '',
                description: bot.description || '',
                strategy_type: bot.strategy_type || '',
                broker_connection_id: bot.broker_connection_id || '',
                min_confidence_score: bot.min_confidence_score != null ? bot.min_confidence_score : '',
                order_type: bot.order_type || 'MARKET',
                time_in_force: bot.time_in_force || 'DAY',
                position_sizing_method: bot.position_sizing_method || '',
                fixed_quantity: bot.fixed_quantity != null ? bot.fixed_quantity : '',
                portfolio_percent: bot.portfolio_percent != null ? bot.portfolio_percent : '',
                risk_per_trade_pct: bot.risk_per_trade_pct != null ? bot.risk_per_trade_pct : '',
                max_position_value: bot.max_position_value != null ? bot.max_position_value : '',
                max_open_positions: bot.max_open_positions != null ? bot.max_open_positions : '',
                max_daily_trades: bot.max_daily_trades != null ? bot.max_daily_trades : '',
                max_daily_loss: bot.max_daily_loss != null ? bot.max_daily_loss : '',
                stop_loss_pct: bot.stop_loss_pct != null ? bot.stop_loss_pct : '',
                take_profit_pct: bot.take_profit_pct != null ? bot.take_profit_pct : '',
                trailing_stop_pct: bot.trailing_stop_pct != null ? bot.trailing_stop_pct : '',
                max_holding_hours: bot.max_holding_hours != null ? bot.max_holding_hours : '',
                exit_on_signal_reversal: bot.exit_on_signal_reversal || false,
                trading_start_time: bot.trading_start_time || '',
                trading_end_time: bot.trading_end_time || '',
                // Indicator config from bot.config
                ema_fast: bot.config?.indicator_config?.ema_fast ?? '12',
                ema_slow: bot.config?.indicator_config?.ema_slow ?? '26',
                rsi_period: bot.config?.indicator_config?.rsi_period ?? '14',
                volume_multiplier: bot.config?.indicator_config?.volume_multiplier ?? '1.5',
                rsi_oversold: bot.config?.indicator_config?.rsi_oversold ?? '30',
                rsi_overbought: bot.config?.indicator_config?.rsi_overbought ?? '70',
                bb_period: bot.config?.indicator_config?.bb_period ?? '20',
                bb_std: bot.config?.indicator_config?.bb_std ?? '2',
            });
        } else {
            setBotEditing(null);
            setBotForm({ ...INITIAL_BOT_FORM });
        }
        setBotDialog(true);
    };

    const handleBotFormChange = (field, value) => {
        setBotForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleBotSave = async () => {
        if (!botForm.name) {
            setSnackbar({ message: 'Bot name is required', severity: 'error' });
            return;
        }
        if (!botForm.strategy_type) {
            setSnackbar({ message: 'Strategy type is required', severity: 'error' });
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                name: botForm.name,
                description: botForm.description || null,
                strategy_type: botForm.strategy_type,
                broker_connection_id: botForm.broker_connection_id || null,
                min_confidence_score: botForm.min_confidence_score !== '' ? Number(botForm.min_confidence_score) : null,
                order_type: botForm.order_type || null,
                time_in_force: botForm.time_in_force || null,
                position_sizing_method: botForm.position_sizing_method || null,
                fixed_quantity: botForm.fixed_quantity !== '' ? Number(botForm.fixed_quantity) : null,
                portfolio_percent: botForm.portfolio_percent !== '' ? Number(botForm.portfolio_percent) : null,
                risk_per_trade_pct: botForm.risk_per_trade_pct !== '' ? Number(botForm.risk_per_trade_pct) : null,
                max_position_value: botForm.max_position_value !== '' ? Number(botForm.max_position_value) : null,
                max_open_positions: botForm.max_open_positions !== '' ? Number(botForm.max_open_positions) : null,
                max_daily_trades: botForm.max_daily_trades !== '' ? Number(botForm.max_daily_trades) : null,
                max_daily_loss: botForm.max_daily_loss !== '' ? Number(botForm.max_daily_loss) : null,
                stop_loss_pct: botForm.stop_loss_pct !== '' ? Number(botForm.stop_loss_pct) : null,
                take_profit_pct: botForm.take_profit_pct !== '' ? Number(botForm.take_profit_pct) : null,
                trailing_stop_pct: botForm.trailing_stop_pct !== '' ? Number(botForm.trailing_stop_pct) : null,
                max_holding_hours: botForm.max_holding_hours !== '' ? Number(botForm.max_holding_hours) : null,
                exit_on_signal_reversal: botForm.exit_on_signal_reversal,
                trading_start_time: botForm.trading_start_time || null,
                trading_end_time: botForm.trading_end_time || null,
            };
            // Add indicator config for MOMENTUM / MEAN_REVERSION strategies
            if (botForm.strategy_type === 'MOMENTUM') {
                payload.config = {
                    indicator_config: {
                        ema_fast: Number(botForm.ema_fast) || 12,
                        ema_slow: Number(botForm.ema_slow) || 26,
                        rsi_period: Number(botForm.rsi_period) || 14,
                        volume_multiplier: Number(botForm.volume_multiplier) || 1.5,
                    }
                };
            } else if (botForm.strategy_type === 'MEAN_REVERSION') {
                payload.config = {
                    indicator_config: {
                        rsi_period: Number(botForm.rsi_period) || 14,
                        rsi_oversold: Number(botForm.rsi_oversold) || 30,
                        rsi_overbought: Number(botForm.rsi_overbought) || 70,
                        bb_period: Number(botForm.bb_period) || 20,
                        bb_std: Number(botForm.bb_std) || 2,
                    }
                };
            }
            if (botEditing) {
                await axiosInstance.put(`/api/trade-execution/trading-bots/${botEditing.id}`, payload);
                setSnackbar({ message: 'Trading bot updated successfully', severity: 'success' });
            } else {
                await axiosInstance.post('/api/trade-execution/trading-bots', payload);
                setSnackbar({ message: 'Trading bot created successfully', severity: 'success' });
            }
            setBotDialog(false);
            fetchBots();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save trading bot');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmOpen = (action, bot) => {
        setConfirmAction({ action, bot });
        setConfirmDialog(true);
    };

    const handleConfirmExecute = async () => {
        if (!confirmAction) return;
        const { action, bot } = confirmAction;
        setConfirmDialog(false);
        setSubmitting(true);
        try {
            if (action === 'delete') {
                await axiosInstance.delete(`/api/trade-execution/trading-bots/${bot.id}`);
                setSnackbar({ message: `Bot "${bot.name}" deleted successfully`, severity: 'success' });
            } else {
                await axiosInstance.post(`/api/trade-execution/trading-bots/${bot.id}/${action}`);
                const actionLabel = action === 'start' ? 'started' : action === 'pause' ? 'paused' : 'stopped';
                setSnackbar({ message: `Bot "${bot.name}" ${actionLabel} successfully`, severity: 'success' });
            }
            fetchBots();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.message || `Failed to ${action} bot`, severity: 'error' });
        } finally {
            setSubmitting(false);
            setConfirmAction(null);
        }
    };

    // ==================== TAB 1: ACTIVE POSITIONS ====================
    const fetchPositions = useCallback(async () => {
        if (!canView) return;
        setPositionsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: positionsPage + 1, per_page: positionsPerPage });
            if (positionsStatusFilter) params.append('status', positionsStatusFilter);
            let url;
            if (positionsBotFilter) {
                url = `/api/trade-execution/trading-bots/${positionsBotFilter}/positions?${params.toString()}`;
            } else {
                params.append('all_bots', 'true');
                url = `/api/trade-execution/trading-bots/positions?${params.toString()}`;
            }
            const res = await axiosInstance.get(url);
            if (res.data.success) {
                setPositions(res.data.data || []);
                setPositionsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch positions');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch positions');
        } finally {
            setPositionsLoading(false);
        }
    }, [canView, positionsPage, positionsPerPage, positionsBotFilter, positionsStatusFilter]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchPositions();
    }, [fetchPositions, canView, activeTab]);

    // ==================== TAB 2: ACTIVITY LOG ====================
    const fetchLogs = useCallback(async () => {
        if (!canView) return;
        setLogsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: logsPage + 1, per_page: logsPerPage });
            if (logsEventTypeFilter) params.append('event_type', logsEventTypeFilter);
            if (logsLevelFilter) params.append('log_level', logsLevelFilter);
            let url;
            if (logsBotFilter) {
                url = `/api/trade-execution/trading-bots/${logsBotFilter}/logs?${params.toString()}`;
            } else {
                params.append('all_bots', 'true');
                url = `/api/trade-execution/trading-bots/logs?${params.toString()}`;
            }
            const res = await axiosInstance.get(url);
            if (res.data.success) {
                setLogs(res.data.data || []);
                setLogsTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch activity logs');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch activity logs');
        } finally {
            setLogsLoading(false);
        }
    }, [canView, logsPage, logsPerPage, logsBotFilter, logsEventTypeFilter, logsLevelFilter]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchLogs();
    }, [fetchLogs, canView, activeTab]);

    // Auto-refresh logs every 10 seconds when tab is active
    useEffect(() => {
        if (!canView || activeTab !== 2) {
            if (logsIntervalRef.current) {
                clearInterval(logsIntervalRef.current);
                logsIntervalRef.current = null;
            }
            return;
        }
        logsIntervalRef.current = setInterval(() => {
            fetchLogs();
        }, 10000);
        return () => {
            if (logsIntervalRef.current) {
                clearInterval(logsIntervalRef.current);
                logsIntervalRef.current = null;
            }
        };
    }, [canView, activeTab, fetchLogs]);

    // ==================== TAB 3: PERFORMANCE ====================
    const fetchPerformance = useCallback(async () => {
        if (!canView) return;
        setPerformanceLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/trade-execution/trading-bots/performance');
            if (res.data.success) {
                setPerformance(res.data.data || []);
            } else {
                setError(res.data.message || 'Failed to fetch performance data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch performance data');
        } finally {
            setPerformanceLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchPerformance();
    }, [fetchPerformance, canView, activeTab]);

    // ==================== RENDER HELPERS ====================
    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    const getConfirmDialogText = () => {
        if (!confirmAction) return { title: '', message: '' };
        const { action, bot } = confirmAction;
        switch (action) {
            case 'start':
                return {
                    title: 'Start Bot',
                    message: `Are you sure you want to start the trading bot "${bot.name}"? It will begin evaluating signals and placing trades.`,
                };
            case 'pause':
                return {
                    title: 'Pause Bot',
                    message: `Are you sure you want to pause the trading bot "${bot.name}"? It will stop evaluating new signals but existing positions remain open.`,
                };
            case 'stop':
                return {
                    title: 'Stop Bot',
                    message: `Are you sure you want to stop the trading bot "${bot.name}"? This will halt all bot activity.`,
                };
            case 'delete':
                return {
                    title: 'Delete Bot',
                    message: `Are you sure you want to delete the trading bot "${bot.name}"? This action cannot be undone.`,
                };
            default:
                return { title: '', message: '' };
        }
    };

    // ==================== RENDER: TAB 0 - BOT MANAGEMENT ====================
    const renderBotManagement = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Bot Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchBots}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button
                            startIcon={<MuiIcons.Add />}
                            variant="contained"
                            onClick={() => handleBotDialogOpen()}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            Create Bot
                        </Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {botsLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Name</TableCell>
                                        <TableCell sx={headCellSx}>Strategy</TableCell>
                                        <TableCell sx={headCellSx}>Status</TableCell>
                                        <TableCell sx={headCellSx}>Symbols</TableCell>
                                        <TableCell sx={headCellSx}>Open Positions</TableCell>
                                        <TableCell sx={headCellSx}>Daily Trades</TableCell>
                                        <TableCell sx={headCellSx}>Daily P&L</TableCell>
                                        <TableCell sx={headCellSx}>Last Run</TableCell>
                                        <TableCell sx={headCellSx}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bots.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.SmartToy sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No trading bots configured</Typography>
                                                <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>Create a new bot to get started</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : bots.map((bot) => (
                                        <TableRow key={bot.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {bot.name || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={BotStrategyTypeLabels[bot.strategy_type] || bot.strategy_type}
                                                    size="small"
                                                    color={BotStrategyTypeColors[bot.strategy_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={BotStatusLabels[bot.status] || bot.status}
                                                    size="small"
                                                    color={BotStatusColors[bot.status] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.82rem' }}>
                                                {(bot.symbols_json && bot.symbols_json.length > 0) ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                                                        {bot.symbols_json.slice(0, 3).map((sym, idx) => (
                                                            <Chip key={idx} label={sym} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                                                        ))}
                                                        {bot.symbols_json.length > 3 && (
                                                            <Chip label={`+${bot.symbols_json.length - 3}`} size="small" variant="outlined" sx={{ fontFamily, fontSize: '0.68rem', height: 20 }} />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>--</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {bot.open_positions_count != null ? bot.open_positions_count : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {bot.daily_trades_count != null ? bot.daily_trades_count : '--'}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontFamily,
                                                fontWeight: 600,
                                                fontSize: '0.82rem',
                                                color: bot.daily_pnl > 0 ? '#10b981' : bot.daily_pnl < 0 ? '#ef4444' : '#1e293b',
                                            }}>
                                                {bot.daily_pnl != null ? formatPrice(bot.daily_pnl) : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {formatDateTime(bot.last_run_at)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {(bot.status === 'STOPPED' || bot.status === 'PAUSED') && canEdit && (
                                                        <Tooltip title="Start">
                                                            <IconButton size="small" onClick={() => handleConfirmOpen('start', bot)}>
                                                                <MuiIcons.PlayArrow sx={{ fontSize: 18, color: '#10b981' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {bot.status === 'ACTIVE' && canEdit && (
                                                        <Tooltip title="Pause">
                                                            <IconButton size="small" onClick={() => handleConfirmOpen('pause', bot)}>
                                                                <MuiIcons.Pause sx={{ fontSize: 18, color: '#f59e0b' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(bot.status === 'ACTIVE' || bot.status === 'PAUSED') && canEdit && (
                                                        <Tooltip title="Stop">
                                                            <IconButton size="small" onClick={() => handleConfirmOpen('stop', bot)}>
                                                                <MuiIcons.Stop sx={{ fontSize: 18, color: '#ef4444' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(bot.status === 'STOPPED' || bot.status === 'PAUSED') && canEdit && (
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" onClick={() => handleBotDialogOpen(bot)}>
                                                                <MuiIcons.Edit sx={{ fontSize: 18, color: '#64748b' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {bot.status === 'STOPPED' && canDelete && (
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => handleConfirmOpen('delete', bot)}>
                                                                <MuiIcons.Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={botsTotal}
                            page={botsPage}
                            onPageChange={(_, newPage) => setBotsPage(newPage)}
                            rowsPerPage={botsPerPage}
                            onRowsPerPageChange={(e) => { setBotsPerPage(parseInt(e.target.value, 10)); setBotsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily }, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 1 - ACTIVE POSITIONS ====================
    const renderPositions = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Active Positions
                </Typography>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={fetchPositions}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ fontFamily }}>Bot</InputLabel>
                    <Select
                        value={positionsBotFilter}
                        onChange={(e) => { setPositionsBotFilter(e.target.value); setPositionsPage(0); }}
                        label="Bot"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All Bots</MenuItem>
                        {bots.map((b) => (
                            <MenuItem key={b.id} value={b.id} sx={{ fontFamily }}>{b.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ fontFamily }}>Status</InputLabel>
                    <Select
                        value={positionsStatusFilter}
                        onChange={(e) => { setPositionsStatusFilter(e.target.value); setPositionsPage(0); }}
                        label="Status"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All Statuses</MenuItem>
                        {Object.entries(BotPositionStatus).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{BotPositionStatusLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {positionsLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Bot Name</TableCell>
                                        <TableCell sx={headCellSx}>Symbol</TableCell>
                                        <TableCell sx={headCellSx}>Direction</TableCell>
                                        <TableCell sx={headCellSx}>Qty</TableCell>
                                        <TableCell sx={headCellSx}>Entry Price</TableCell>
                                        <TableCell sx={headCellSx}>Unrealized P&L</TableCell>
                                        <TableCell sx={headCellSx}>SL Price</TableCell>
                                        <TableCell sx={headCellSx}>TP Price</TableCell>
                                        <TableCell sx={headCellSx}>Opened At</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {positions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.AccountBalance sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No positions found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : positions.map((pos) => (
                                        <TableRow key={pos.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {pos.bot_name || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {pos.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={pos.direction || '--'}
                                                    size="small"
                                                    color={pos.direction === 'LONG' ? 'success' : pos.direction === 'SHORT' ? 'error' : 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatNumber(pos.quantity)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatPrice(pos.entry_price)}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontFamily,
                                                fontWeight: 600,
                                                fontSize: '0.82rem',
                                                color: pos.unrealized_pnl > 0 ? '#10b981' : pos.unrealized_pnl < 0 ? '#ef4444' : '#1e293b',
                                            }}>
                                                {pos.unrealized_pnl != null ? formatPrice(pos.unrealized_pnl) : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatPrice(pos.stop_loss_price)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatPrice(pos.take_profit_price)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {formatDateTime(pos.opened_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={positionsTotal}
                            page={positionsPage}
                            onPageChange={(_, newPage) => setPositionsPage(newPage)}
                            rowsPerPage={positionsPerPage}
                            onRowsPerPageChange={(e) => { setPositionsPerPage(parseInt(e.target.value, 10)); setPositionsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily }, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 2 - ACTIVITY LOG ====================
    const renderActivityLog = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                        Activity Log
                    </Typography>
                    <Chip
                        icon={<MuiIcons.AutorenewOutlined sx={{ fontSize: 14 }} />}
                        label="Auto-refresh: 10s"
                        size="small"
                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24, bgcolor: '#dcfce7', color: '#16a34a' }}
                    />
                </Box>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={fetchLogs}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ fontFamily }}>Bot</InputLabel>
                    <Select
                        value={logsBotFilter}
                        onChange={(e) => { setLogsBotFilter(e.target.value); setLogsPage(0); }}
                        label="Bot"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All Bots</MenuItem>
                        {bots.map((b) => (
                            <MenuItem key={b.id} value={b.id} sx={{ fontFamily }}>{b.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel sx={{ fontFamily }}>Event Type</InputLabel>
                    <Select
                        value={logsEventTypeFilter}
                        onChange={(e) => { setLogsEventTypeFilter(e.target.value); setLogsPage(0); }}
                        label="Event Type"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All Events</MenuItem>
                        {Object.entries(BotLogEventType).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{BotLogEventTypeLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ fontFamily }}>Level</InputLabel>
                    <Select
                        value={logsLevelFilter}
                        onChange={(e) => { setLogsLevelFilter(e.target.value); setLogsPage(0); }}
                        label="Level"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="" sx={{ fontFamily }}>All Levels</MenuItem>
                        {Object.entries(BotLogLevel).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{BotLogLevelLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {logsLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Time</TableCell>
                                        <TableCell sx={headCellSx}>Bot</TableCell>
                                        <TableCell sx={headCellSx}>Event Type</TableCell>
                                        <TableCell sx={headCellSx}>Level</TableCell>
                                        <TableCell sx={headCellSx}>Message</TableCell>
                                        <TableCell sx={headCellSx}>Signal ID</TableCell>
                                        <TableCell sx={headCellSx}>Order ID</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.Article sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No activity logs found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : logs.map((log) => (
                                        <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                {formatDateTime(log.created_at || log.timestamp)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {log.bot_name || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={BotLogEventTypeLabels[log.event_type] || log.event_type}
                                                    size="small"
                                                    color={BotLogEventTypeColors[log.event_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={BotLogLevelLabels[log.log_level] || log.log_level}
                                                    size="small"
                                                    color={BotLogLevelColors[log.log_level] || 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#475569', fontSize: '0.82rem', maxWidth: 300 }}>
                                                <Tooltip title={log.message || ''}>
                                                    <Typography
                                                        noWrap
                                                        sx={{ fontFamily, fontSize: '0.82rem', color: '#475569', maxWidth: 300 }}
                                                    >
                                                        {log.message || '--'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                {log.signal_id || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                {log.order_id || '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={logsTotal}
                            page={logsPage}
                            onPageChange={(_, newPage) => setLogsPage(newPage)}
                            rowsPerPage={logsPerPage}
                            onRowsPerPageChange={(e) => { setLogsPerPage(parseInt(e.target.value, 10)); setLogsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily }, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 3 - PERFORMANCE ====================
    const renderPerformance = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Performance Overview
                </Typography>
                <Button
                    startIcon={<MuiIcons.Refresh />}
                    onClick={fetchPerformance}
                    size="small"
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    Refresh
                </Button>
            </Box>

            {performanceLoading ? renderSkeleton(3) : (
                performance.length === 0 ? (
                    <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 4, textAlign: 'center' }}>
                        <MuiIcons.BarChart sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No performance data available</Typography>
                        <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>Performance metrics will appear after bots complete trades</Typography>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {performance.map((perf) => (
                            <Paper key={perf.bot_id || perf.id} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem', mb: 2 }}>
                                    {perf.bot_name || `Bot #${perf.bot_id}`}
                                    {perf.strategy_type && (
                                        <Chip
                                            label={BotStrategyTypeLabels[perf.strategy_type] || perf.strategy_type}
                                            size="small"
                                            color={BotStrategyTypeColors[perf.strategy_type] || 'default'}
                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24, ml: 1.5 }}
                                        />
                                    )}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: '10px',
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>
                                                Total P&L
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                fontSize: '1.1rem',
                                                color: perf.total_pnl > 0 ? '#10b981' : perf.total_pnl < 0 ? '#ef4444' : '#1e293b',
                                            }}>
                                                {perf.total_pnl != null ? formatPrice(perf.total_pnl) : '--'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: '10px',
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>
                                                Win Rate
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                fontSize: '1.1rem',
                                                color: perf.win_rate >= 50 ? '#10b981' : perf.win_rate != null ? '#ef4444' : '#1e293b',
                                            }}>
                                                {perf.win_rate != null ? formatPercent(perf.win_rate) : '--'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: '10px',
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>
                                                Total Trades
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                                                {perf.total_trades != null ? perf.total_trades : '--'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: '10px',
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>
                                                Avg Holding Hrs
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                                                {perf.avg_holding_hours != null ? parseFloat(perf.avg_holding_hours).toFixed(1) : '--'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: '10px',
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>
                                                Winning
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#10b981' }}>
                                                {perf.winning_trades != null ? perf.winning_trades : '--'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                borderRadius: '10px',
                                                bgcolor: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em', mb: 0.5 }}>
                                                Losing
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#ef4444' }}>
                                                {perf.losing_trades != null ? perf.losing_trades : '--'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                    </Box>
                )
            )}
        </Box>
    );

    // ==================== RENDER: BOT CREATE/EDIT DIALOG ====================
    const renderBotDialog = () => (
        <Dialog open={botDialog} onClose={() => setBotDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5 }}>
                {botEditing ? 'Edit Trading Bot' : 'Create Trading Bot'}
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Section 1: Basic Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, mt: 0.5 }}>
                    Basic Information
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Bot Name"
                            value={botForm.name}
                            onChange={(e) => handleBotFormChange('name', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel sx={{ fontFamily }}>Strategy Type</InputLabel>
                            <Select
                                value={botForm.strategy_type}
                                onChange={(e) => handleBotFormChange('strategy_type', e.target.value)}
                                label="Strategy Type"
                                sx={{ fontFamily }}
                            >
                                {Object.entries(BotStrategyType).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{BotStrategyTypeLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Description"
                            value={botForm.description}
                            onChange={(e) => handleBotFormChange('description', e.target.value)}
                            multiline
                            rows={2}
                            sx={{ fontFamily, '& textarea': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel sx={{ fontFamily }}>Broker Connection</InputLabel>
                            <Select
                                value={botForm.broker_connection_id}
                                onChange={(e) => handleBotFormChange('broker_connection_id', e.target.value)}
                                label="Broker Connection"
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="" sx={{ fontFamily }}>-- Select Broker --</MenuItem>
                                {brokerConnections.map((bc) => (
                                    <MenuItem key={bc.id} value={bc.id} sx={{ fontFamily }}>
                                        {bc.broker_name ? `${bc.broker_name}` : `Broker #${bc.id}`}
                                        {bc.account_id ? ` (${bc.account_id})` : ''}
                                        {bc.connection_status === 'CONNECTED' ? ' ✓' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* ── Indicator Config (MOMENTUM) ── */}
                    {botForm.strategy_type === 'MOMENTUM' && (
                        <>
                            <Grid item xs={12}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: ACCENT, mt: 0.5 }}>
                                    Momentum Indicator Settings
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                    BUY when EMA(fast) {'>'} EMA(slow) + RSI {'>'} 50 + Volume spike. SELL on reverse.
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="EMA Fast Period" type="number"
                                    value={botForm.ema_fast} onChange={(e) => handleBotFormChange('ema_fast', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="EMA Slow Period" type="number"
                                    value={botForm.ema_slow} onChange={(e) => handleBotFormChange('ema_slow', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="RSI Period" type="number"
                                    value={botForm.rsi_period} onChange={(e) => handleBotFormChange('rsi_period', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="Volume Multiplier" type="number"
                                    value={botForm.volume_multiplier} onChange={(e) => handleBotFormChange('volume_multiplier', e.target.value)}
                                    inputProps={{ step: 0.1 }}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                        </>
                    )}
                    {/* ── Indicator Config (MEAN_REVERSION) ── */}
                    {botForm.strategy_type === 'MEAN_REVERSION' && (
                        <>
                            <Grid item xs={12}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: ACCENT, mt: 0.5 }}>
                                    Mean Reversion Indicator Settings
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                    BUY when RSI {'<'} oversold + price {'<'} Bollinger lower. SELL on reverse.
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="RSI Period" type="number"
                                    value={botForm.rsi_period} onChange={(e) => handleBotFormChange('rsi_period', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="RSI Oversold" type="number"
                                    value={botForm.rsi_oversold} onChange={(e) => handleBotFormChange('rsi_oversold', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="RSI Overbought" type="number"
                                    value={botForm.rsi_overbought} onChange={(e) => handleBotFormChange('rsi_overbought', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="Bollinger Period" type="number"
                                    value={botForm.bb_period} onChange={(e) => handleBotFormChange('bb_period', e.target.value)}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="Bollinger StdDev" type="number"
                                    value={botForm.bb_std} onChange={(e) => handleBotFormChange('bb_std', e.target.value)}
                                    inputProps={{ step: 0.5 }}
                                    sx={{ fontFamily, '& input': { fontFamily } }} />
                            </Grid>
                        </>
                    )}
                </Grid>

                {/* Section 2: Order Settings */}
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, mt: 3 }}>
                    Order Settings
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Min Confidence Score"
                            type="number"
                            value={botForm.min_confidence_score}
                            onChange={(e) => handleBotFormChange('min_confidence_score', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0, max: 100, step: '0.1' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Order Type</InputLabel>
                            <Select
                                value={botForm.order_type}
                                onChange={(e) => handleBotFormChange('order_type', e.target.value)}
                                label="Order Type"
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="MARKET" sx={{ fontFamily }}>Market</MenuItem>
                                <MenuItem value="LIMIT" sx={{ fontFamily }}>Limit</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Time in Force</InputLabel>
                            <Select
                                value={botForm.time_in_force}
                                onChange={(e) => handleBotFormChange('time_in_force', e.target.value)}
                                label="Time in Force"
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="DAY" sx={{ fontFamily }}>DAY</MenuItem>
                                <MenuItem value="GTC" sx={{ fontFamily }}>GTC</MenuItem>
                                <MenuItem value="IOC" sx={{ fontFamily }}>IOC</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Section 3: Position Sizing */}
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, mt: 3 }}>
                    Position Sizing
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Position Sizing Method</InputLabel>
                            <Select
                                value={botForm.position_sizing_method}
                                onChange={(e) => handleBotFormChange('position_sizing_method', e.target.value)}
                                label="Position Sizing Method"
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="" sx={{ fontFamily }}>None</MenuItem>
                                {Object.entries(PositionSizingMethod).map(([key, val]) => (
                                    <MenuItem key={key} value={val} sx={{ fontFamily }}>{PositionSizingMethodLabels[val]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {botForm.position_sizing_method === 'FIXED_QUANTITY' && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Fixed Quantity"
                                type="number"
                                value={botForm.fixed_quantity}
                                onChange={(e) => handleBotFormChange('fixed_quantity', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                    )}
                    {botForm.position_sizing_method === 'PERCENT_OF_PORTFOLIO' && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Portfolio Percent (%)"
                                type="number"
                                value={botForm.portfolio_percent}
                                onChange={(e) => handleBotFormChange('portfolio_percent', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 0, max: 100, step: '0.1' }}
                            />
                        </Grid>
                    )}
                    {botForm.position_sizing_method === 'RISK_BASED' && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Risk Per Trade (%)"
                                type="number"
                                value={botForm.risk_per_trade_pct}
                                onChange={(e) => handleBotFormChange('risk_per_trade_pct', e.target.value)}
                                sx={{ fontFamily, '& input': { fontFamily } }}
                                inputProps={{ min: 0, max: 100, step: '0.1' }}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Position Value ($)"
                            type="number"
                            value={botForm.max_position_value}
                            onChange={(e) => handleBotFormChange('max_position_value', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                    </Grid>
                </Grid>

                {/* Section 4: Risk Limits */}
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, mt: 3 }}>
                    Risk Limits
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Open Positions"
                            type="number"
                            value={botForm.max_open_positions}
                            onChange={(e) => handleBotFormChange('max_open_positions', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Daily Trades"
                            type="number"
                            value={botForm.max_daily_trades}
                            onChange={(e) => handleBotFormChange('max_daily_trades', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Daily Loss ($)"
                            type="number"
                            value={botForm.max_daily_loss}
                            onChange={(e) => handleBotFormChange('max_daily_loss', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                    </Grid>
                </Grid>

                {/* Section 5: Exit Rules */}
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, mt: 3 }}>
                    Exit Rules
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Stop Loss (%)"
                            type="number"
                            value={botForm.stop_loss_pct}
                            onChange={(e) => handleBotFormChange('stop_loss_pct', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0, max: 100, step: '0.1' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Take Profit (%)"
                            type="number"
                            value={botForm.take_profit_pct}
                            onChange={(e) => handleBotFormChange('take_profit_pct', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0, max: 100, step: '0.1' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Trailing Stop (%)"
                            type="number"
                            value={botForm.trailing_stop_pct}
                            onChange={(e) => handleBotFormChange('trailing_stop_pct', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0, max: 100, step: '0.1' }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Max Holding Hours"
                            type="number"
                            value={botForm.max_holding_hours}
                            onChange={(e) => handleBotFormChange('max_holding_hours', e.target.value)}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={botForm.exit_on_signal_reversal}
                                    onChange={(e) => handleBotFormChange('exit_on_signal_reversal', e.target.checked)}
                                    sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontSize: '0.875rem' }}>Exit on Signal Reversal</Typography>}
                        />
                    </Grid>
                </Grid>

                {/* Section 6: Trading Schedule */}
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#475569', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, mt: 3 }}>
                    Trading Schedule
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Trading Start Time"
                            type="time"
                            value={botForm.trading_start_time}
                            onChange={(e) => handleBotFormChange('trading_start_time', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Trading End Time"
                            type="time"
                            value={botForm.trading_end_time}
                            onChange={(e) => handleBotFormChange('trading_end_time', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ fontFamily, '& input': { fontFamily } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={() => setBotDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleBotSave}
                    variant="contained"
                    disabled={submitting || !botForm.name || !botForm.strategy_type}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Saving...' : (botEditing ? 'Update Bot' : 'Create Bot')}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== RENDER: CONFIRMATION DIALOG ====================
    const renderConfirmDialog = () => {
        const { title, message } = getConfirmDialogText();
        const isDelete = confirmAction?.action === 'delete';
        return (
            <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
                    {title}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#475569', fontSize: '0.875rem' }}>
                        {message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setConfirmDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmExecute}
                        variant="contained"
                        color={isDelete ? 'error' : 'primary'}
                        disabled={submitting}
                        sx={{
                            fontFamily,
                            textTransform: 'none',
                            fontWeight: 600,
                            ...(isDelete ? {} : { bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }),
                        }}
                    >
                        {submitting ? 'Processing...' : (confirmAction?.action ? confirmAction.action.charAt(0).toUpperCase() + confirmAction.action.slice(1) : 'Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view Trading Bots. Required permission: <strong>tr-tee-trading-bots</strong>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MuiIcons.SmartToy sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Trading Bots
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Configure, monitor, and manage automated trading bots
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            minHeight: 48,
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                >
                    <Tab icon={<MuiIcons.SmartToy sx={{ fontSize: 18 }} />} iconPosition="start" label="Bot Management" />
                    <Tab icon={<MuiIcons.AccountBalance sx={{ fontSize: 18 }} />} iconPosition="start" label="Active Positions" />
                    <Tab icon={<MuiIcons.Article sx={{ fontSize: 18 }} />} iconPosition="start" label="Activity Log" />
                    <Tab icon={<MuiIcons.BarChart sx={{ fontSize: 18 }} />} iconPosition="start" label="Performance" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderBotManagement()}
            {activeTab === 1 && renderPositions()}
            {activeTab === 2 && renderActivityLog()}
            {activeTab === 3 && renderPerformance()}

            {/* Dialogs */}
            {renderBotDialog()}
            {renderConfirmDialog()}

            {/* Snackbar */}
            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {snackbar ? (
                    <Alert
                        onClose={() => setSnackbar(null)}
                        severity={snackbar.severity}
                        sx={{ fontFamily, fontWeight: 600 }}
                    >
                        {snackbar.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default TradingBotPage;
